/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/SignalsManager.tsx
 * FILE_ID: EMAILHUB-READERHEADER-LANES-SIGNALSMGR-001
 * PURPOSE: Overlay listing EVERY structured signal with copy + tag management.
 * ROLE IN ARCHITECTURE: Modal leaf opened from the Intelligence lane's "+N".
 * MAIN EXPORTS: SignalsManager, SIGNALS_MANAGER_SEARCH_THRESHOLD.
 * NON-RESPONSIBILITIES: No signal ranking; no lane rendering.
 * NOTES FOR FUTURE AI: Copy rows implement the 1.5s "Copied" ack with the
 *   clipboard-denied fallback (plan Intelligence lane). Tag add/remove is
 *   the live EmailTagChips — never a lane-local tag editor.
 */
import { useEffect, useRef, useState } from 'react'
import { ClipboardCopy, Search, X } from 'lucide-react'

import type { EmailActionSignal } from '../../action-signals'
import { EmailTagChips } from '../../components/EmailTagChips'
import { SenderSignalBadges } from '../../sender-identity'
import type { Email } from '../../hooks/useEmails'

/** Above this many rows the manager grows its search field. */
export const SIGNALS_MANAGER_SEARCH_THRESHOLD = 12
const COPY_ACK_MS = 1500

interface CopyRow {
  id: string
  label: string
  value: string
}

export interface SignalsManagerProps {
  open: boolean
  email: Email
  verificationCode: EmailActionSignal | null
  invoiceNumber: EmailActionSignal | null
  onClose: () => void
  onRefetch?: () => void
}

function buildCopyRows(
  email: Email,
  verification: EmailActionSignal | null,
  invoice: EmailActionSignal | null,
): CopyRow[] {
  const rows: CopyRow[] = []
  if (verification) {
    rows.push({ id: 'verification', label: 'Verification code', value: verification.display_value })
  }
  if (invoice) {
    rows.push({ id: 'invoice', label: 'Invoice number', value: invoice.display_value })
  }
  if (email.ai_summary?.trim()) {
    rows.push({ id: 'ai_summary', label: 'AI summary', value: email.ai_summary })
  }
  return rows
}

/**
 * Full-inventory signals overlay.
 *
 * Inputs: open flag, email, resolved signals, close/refetch callbacks.
 * Output: a centred modal (portal-free — it sits above the reader, not
 * inside a clipped ancestor) with copy rows, provenance badges, and the
 * live tag editor. Search appears past 12 rows (rows + tags).
 * Failure mode: clipboard denial flips the row into an inline error state
 * instead of a silent no-op.
 */
export function SignalsManager({
  open,
  email,
  verificationCode,
  invoiceNumber,
  onClose,
  onRefetch,
}: SignalsManagerProps) {
  const [query, setQuery] = useState('')
  const [ack, setAck] = useState<{ id: string; ok: boolean } | null>(null)
  const ackTimer = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (ackTimer.current != null) window.clearTimeout(ackTimer.current)
    }
  }, [])

  if (!open) return null

  const copyRows = buildCopyRows(email, verificationCode, invoiceNumber)
  const totalRows = copyRows.length + (email.tags?.length ?? 0)
  const searchable = totalRows > SIGNALS_MANAGER_SEARCH_THRESHOLD
  const needle = query.trim().toLowerCase()
  const visibleRows =
    searchable && needle !== ''
      ? copyRows.filter(
          (row) =>
            row.label.toLowerCase().includes(needle) ||
            row.value.toLowerCase().includes(needle),
        )
      : copyRows

  const copy = async (row: CopyRow) => {
    let ok = true
    try {
      await navigator.clipboard.writeText(row.value)
    } catch {
      ok = false
    }
    setAck({ id: row.id, ok })
    if (ackTimer.current != null) window.clearTimeout(ackTimer.current)
    ackTimer.current = window.setTimeout(() => setAck(null), COPY_ACK_MS)
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/30 p-4"
      role="presentation"
      data-testid="signals-manager"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Signals Manager"
        className="w-full max-w-md rounded-lg border border-border bg-card p-3 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Signals
          </h2>
          <button
            type="button"
            aria-label="Close Signals Manager"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
        {searchable && (
          <div className="mb-2 flex items-center gap-1.5">
            <Search className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
            <input
              type="text"
              value={query}
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search signals…"
              aria-label="Search signals"
              className="w-full rounded border border-border bg-transparent px-1.5 py-1 text-xs outline-none focus:border-primary"
            />
          </div>
        )}
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          {visibleRows.map((row) => (
            <div key={row.id} className="flex items-center justify-between gap-2 text-xs">
              <div className="min-w-0">
                <div className="font-medium text-foreground">{row.label}</div>
                <div className="truncate text-muted-foreground" title={row.value}>
                  {row.value}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void copy(row)}
                data-testid={`copy-signal-${row.id}`}
                className="inline-flex shrink-0 items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-accent"
              >
                <ClipboardCopy className="h-3 w-3" aria-hidden />
                {ack?.id === row.id
                  ? ack.ok
                    ? 'Copied'
                    : 'Clipboard blocked'
                  : 'Copy'}
              </button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-1 pt-1">
            <SenderSignalBadges email={email} />
          </div>
          <div className="border-t border-border pt-2">
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Tags
            </div>
            {/* Live tag editor — add/remove mutate through the shared hook. */}
            <EmailTagChips
              emailId={email.id}
              tags={email.tags}
              size="xs"
              onChanged={onRefetch}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
